import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { PrazoAgrupado } from '@/utils/equiplexQualidade';
import { margemDoRotuloEmPe, type MedidasRotulo } from '@/utils/rotulosGrafico';
import { fmtDias, fmtUn, mesCurto } from '@/utils/painelFormat';
import AreaDoGrafico from '../AreaDoGrafico';
import { ALTURA_GRAFICO, FONTE_ROTULO, tickEixo, type CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import PainelCard from '../PainelCard';
import { rotuloNoTopo } from '../rotuloBarra';
import TooltipBox from '../TooltipBox';

type Ponto = PrazoAgrupado & { mesCurto: string };

/** Duas colunas por mês (médio e 9 em 10): deitado, ~92 px por mês; em pé, ~44 (03/11/2026). */
const MEDIDAS: MedidasRotulo = { reservado: 64, deitado: 92, emPe: 44 };

/** Prazo de liberação mês a mês: a média e o prazo em que 9 de cada 10 lotes já saíram, com o número nas duas. */
const PrazoMensalChart: FC<{ meses: PrazoAgrupado[]; cores: CoresPainel }> = ({ meses, cores }) => {
  const dados: Ponto[] = meses.map((m) => ({ ...m, mesCurto: mesCurto(m.rotulo) }));
  const topoEmPe = margemDoRotuloEmPe(dados.flatMap((d) => [fmtDias(d.prazoMedio), fmtDias(d.prazoP90)]), FONTE_ROTULO);

  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const m = active && payload?.[0] ? (payload[0].payload as Ponto) : null;
    if (!m) return null;
    return (
      <TooltipBox
        titulo={m.rotulo}
        linhas={[
          { rotulo: 'Prazo médio', valor: fmtDias(m.prazoMedio), cor: cores.programado },
          { rotulo: '9 em 10 lotes em até', valor: fmtDias(m.prazoP90), cor: cores.produzido },
          { rotulo: 'Lotes com prazo', valor: `${fmtUn(m.comPrazo)} de ${fmtUn(m.lotes)}` },
        ]}
      />
    );
  };

  return (
    <PainelCard
      testId="qualidade-prazo-mes"
      titulo="Prazo de liberação por mês"
      subtitulo="Pelo mês de produção da entrada no CQ, com os meses de antes do período (até 6 meses). No mês aberto, só entram os lotes que já saíram."
    >
      <Legenda
        itens={[
          { rotulo: 'Prazo médio', cor: cores.programado },
          { rotulo: '9 em 10 lotes em até (p90)', cor: cores.produzido },
        ]}
      />
      <AreaDoGrafico altura={ALTURA_GRAFICO} categorias={dados.length} medidas={MEDIDAS}>
        {(modo) => (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dados} margin={{ top: modo === 'em-pe' ? Math.max(topoEmPe, 24) : 24, right: 8, bottom: 0, left: 0 }} barGap={4} barCategoryGap="24%">
              <CartesianGrid vertical={false} stroke={cores.grade} />
              <XAxis dataKey="mesCurto" tick={tickEixo(cores)} axisLine={{ stroke: cores.eixo }} tickLine={false} />
              <YAxis allowDecimals={false} unit=" d" tick={tickEixo(cores)} axisLine={false} tickLine={false} width={56} />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar dataKey="prazoMedio" fill={cores.programado} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
                {modo && <LabelList dataKey="prazoMedio" content={rotuloNoTopo(fmtDias, cores.texto, undefined, modo === 'em-pe')} />}
              </Bar>
              <Bar dataKey="prazoP90" fill={cores.produzido} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
                {modo && <LabelList dataKey="prazoP90" content={rotuloNoTopo(fmtDias, cores.texto, undefined, modo === 'em-pe')} />}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </AreaDoGrafico>
    </PainelCard>
  );
};

export default PrazoMensalChart;
